import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { message } from 'antd';
import api from '../services/api';

// Types
interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  /** Auto-login with JWT from accept-invitation flow */
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Safe localStorage functions
const getStoredToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('admin_token');
  }
  return null;
};

const setStoredToken = (token: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('admin_token', token);
  }
};

const removeStoredToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('admin_token');
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async (): Promise<void> => {
    try {
      const token = getStoredToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/profile');
        setUser(response.data.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      removeStoredToken();
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const response = await api.get('/profile');
      setUser(response.data.data);
    } catch {
      // Ignore - user may have been logged out
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/login', { email, password });

      const { token, user: userData } = response.data.data;

      setStoredToken(token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(userData);
      setIsAuthenticated(true);

      message.success('Đăng nhập thành công');
      return true;
    } catch (error: any) {
      // Never log request body (password) or tokens

      // Extract error message from response
      let errorMessage = 'Đăng nhập thất bại';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error message to user
      message.error(errorMessage);

      // Don't redirect - let user stay on login page to try again
      return false;
    }
  };

  const loginWithToken = async (token: string): Promise<boolean> => {
    try {
      setStoredToken(token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/profile');
      setUser(response.data.data);
      setIsAuthenticated(true);
      message.success('Đăng nhập thành công');
      return true;
    } catch {
      removeStoredToken();
      delete api.defaults.headers.common['Authorization'];
      return false;
    }
  };

  const logout = (): void => {
    removeStoredToken();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithToken,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
